import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiScopes } from '@/auth/msalConfig';
import { devMode, devUser } from '@/auth/devMode';
import type {
  Me,
  ModelInfo,
  CompletionRequest,
  CompletionResponse,
  MyUsageResponse,
  Caller,
  CallerStatus,
  BudgetRequestDetail,
  BudgetRequestResult,
  UpdateCallerRequest,
  CreateSystemCallerRequest,
  AdminModel,
  UpsertModelRequest,
  SetModelEnabledRequest,
  ReconcileResult,
  GatewayDefault,
  SetGatewayDefaultRequest,
  SupplierInfo,
  DailyUsagePoint,
  UsageGroupRow,
  PromptTemplateDto,
  SuggestPromptTemplateRequest,
  RequestLogRow,
  AvailableSupplierModel,
  FileTaskRequest,
  FileTaskResponse,
  CompletionStreamHandlers,
  StreamToolCall,
  AvailableMcpServer,
  SavedMcpCredential,
  AdminMcpServer,
  UpsertMcpServerRequest,
  McpServerTool,
  ToolUsageSummaryRow,
  ToolInvocationRow,
  DecisionRequest,
  DecisionResponse
} from '@/types';

// All gateway endpoints live under /api/v1.
const apiBase = `${import.meta.env.VITE_API_BASE ?? '/api'}/v1`;

/** Absolute base of the gateway API (…/api/v1) — used by "copy as code" snippets. */
export const apiBaseUrl = apiBase.startsWith('http')
  ? apiBase
  : `${window.location.origin}${apiBase}`;

/**
 * Error thrown for non-2xx responses. Carries the RFC7807 problem-details
 * `code` and `detail` so pages can render friendly, code-specific messages
 * (e.g. budget_exhausted, supplier_key_missing).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly detail?: string;

  constructor(status: number, message: string, code?: string, detail?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

/**
 * A silent renewal that failed for environmental reasons rather than because consent is needed.
 * These carry no InteractionRequiredAuthError, so they are matched on MSAL's error codes.
 */
function isIframeRenewalFailure(e: unknown): boolean {
  const code = (e as { errorCode?: string } | null)?.errorCode;
  return code === 'monitor_window_timeout'
    || code === 'iframe_closed_prematurely'
    || code === 'block_iframe_reload'
    || code === 'popup_window_error';
}

function usageQuery(from: string, to: string, callerId?: string): string {
  const qs = new URLSearchParams({ from, to });
  if (callerId) qs.set('callerId', callerId);
  return qs.toString();
}

export function useApi() {
  // useMsal throws if the MsalProvider isn't mounted, so guard it for dev mode.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const msal: any = devMode ? null : useMsal();

  async function getToken(): Promise<string | null> {
    if (devMode) return null;
    const account = msal.accounts[0] ?? msal.instance.getActiveAccount();
    if (!account) throw new Error('Not signed in');
    try {
      const result = await msal.instance.acquireTokenSilent({ scopes: apiScopes, account });
      return result.accessToken;
    } catch (e) {
      // InteractionRequiredAuthError is the documented "ask the user" signal, but it is not the
      // only recoverable failure. A hidden-iframe renewal that never lands surfaces as a
      // BrowserAuthError (monitor_window_timeout / iframe or popup blocked), and treating that as
      // fatal strands the user on "Could not load your profile" when one prompt would fix it.
      const recoverable = e instanceof InteractionRequiredAuthError || isIframeRenewalFailure(e);
      if (recoverable) {
        const result = await msal.instance.acquireTokenPopup({ scopes: apiScopes });
        return result.accessToken;
      }
      throw e;
    }
  }

  async function buildHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (devMode) {
      headers['X-Dev-User'] = devUser.objectId;
    } else {
      const token = await getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }

  async function throwApiError(res: Response): Promise<never> {
    let code: string | undefined;
    let detail: string | undefined;
    let title: string | undefined;
    try {
      const problem = await res.json();
      code = typeof problem.code === 'string' ? problem.code : undefined;
      detail = typeof problem.detail === 'string' ? problem.detail : undefined;
      title = typeof problem.title === 'string' ? problem.title : undefined;
    } catch { /* not JSON */ }
    const message = detail ?? title ?? res.statusText;
    throw new ApiError(res.status, `API ${res.status}: ${message}`, code, detail ?? title);
  }

  /**
   * POST /completions/stream — parses the SSE frames and dispatches to the handlers.
   * Validation failures reject with the same ApiError as the sync call (the server refuses
   * before streaming starts); once streaming, failures arrive via onError.
   */
  async function completeStream(
    req: CompletionRequest,
    handlers: CompletionStreamHandlers,
    signal?: AbortSignal
  ): Promise<void> {
    const headers = await buildHeaders();
    headers.Accept = 'text/event-stream';

    const res = await fetch(`${apiBase}/completions/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify(req),
      signal
    });

    const contentType = res.headers.get('content-type') ?? '';
    if (!res.ok || !contentType.includes('text/event-stream')) {
      await throwApiError(res);
    }

    let sawTerminal = false;
    const processFrame = (frame: string) => {
      let event = 'message';
      const dataLines: string[] = [];
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
        // Lines starting ':' are keep-alive comments — ignored.
      }
      if (dataLines.length === 0) return;
      const data = JSON.parse(dataLines.join('\n'));
      if (event === 'delta') handlers.onDelta(data.text ?? '');
      else if (event === 'tool_call') handlers.onToolCall(data as StreamToolCall);
      else if (event === 'done') { sawTerminal = true; handlers.onDone(data as CompletionResponse); }
      else if (event === 'error') { sawTerminal = true; handlers.onError(data.code ?? 'stream_error', data.message ?? 'The stream failed.'); }
    };

    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let boundary;
      while ((boundary = buffer.indexOf('\n\n')) >= 0) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        if (frame.trim()) processFrame(frame);
      }
    }

    if (!sawTerminal) {
      handlers.onError('stream_interrupted', 'The stream ended before completing. Check Usage for what was billed.');
    }
  }

  async function send<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    const headers = await buildHeaders();

    const res = await fetch(`${apiBase}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal
    });
    if (!res.ok) {
      await throwApiError(res);
    }
    if (res.status === 204) return undefined as unknown as T;
    return (await res.json()) as T;
  }

  return {
    me: () => send<Me>('GET', '/me'),
    requestAccess: (justification?: string) =>
      send<Me>('POST', '/access-requests', justification ? { justification } : {}),
    setAlertThreshold: (thresholdPercent: number | null) =>
      send<void>('PUT', '/me/alert-threshold', { thresholdPercent }),
    requestBudgetIncrease: (requestedBudgetUsd: number | null, justification: string) =>
      send<BudgetRequestResult>('POST', '/me/budget-request', {
        requestedBudgetUsd: requestedBudgetUsd ?? undefined,
        justification: justification || undefined
      }),

    listModels: () => send<ModelInfo[]>('GET', '/models'),
    complete: (req: CompletionRequest, signal?: AbortSignal) =>
      send<CompletionResponse>('POST', '/completions', req, signal),
    completeStream,
    fileTask: (req: FileTaskRequest) => send<FileTaskResponse>('POST', '/file-tasks', req),
    // Decisions models: typed answers with probabilities rather than text (kind Decisions only).
    decide: (req: DecisionRequest, signal?: AbortSignal) =>
      send<DecisionResponse>('POST', '/decisions', req, signal),
    listMcpServers: () => send<AvailableMcpServer[]>('GET', '/mcp-servers'),

    // Keys saved against user-credential MCP servers. The key goes to Key Vault and is never
    // read back — the list only says which servers have one, and a four-character hint.
    listMyMcpCredentials: () => send<SavedMcpCredential[]>('GET', '/me/mcp-credentials'),
    saveMyMcpCredential: (serverName: string, apiKey: string) =>
      send<SavedMcpCredential>('PUT', `/me/mcp-credentials/${encodeURIComponent(serverName)}`, { apiKey }),
    forgetMyMcpCredential: (serverName: string) =>
      send<void>('DELETE', `/me/mcp-credentials/${encodeURIComponent(serverName)}`),

    listPromptTemplates: () => send<PromptTemplateDto[]>('GET', '/prompt-templates'),
    suggestPromptTemplate: (req: SuggestPromptTemplateRequest) =>
      send<PromptTemplateDto>('POST', '/prompt-templates/suggestions', req),
    myPromptTemplates: () => send<PromptTemplateDto[]>('GET', '/prompt-templates/mine'),
    adminPendingPromptTemplates: () =>
      send<PromptTemplateDto[]>('GET', '/admin/prompt-templates/pending'),
    adminApprovePromptTemplate: (id: string, notes?: string) =>
      send<PromptTemplateDto>('POST', `/admin/prompt-templates/${id}/approve`, { notes }),
    adminRejectPromptTemplate: (id: string, notes?: string) =>
      send<PromptTemplateDto>('POST', `/admin/prompt-templates/${id}/reject`, { notes }),

    myUsage: (from: string, to: string) =>
      send<MyUsageResponse>('GET', `/usage/me?${usageQuery(from, to)}`),
    myRecentRequests: (top = 50) =>
      send<RequestLogRow[]>('GET', `/usage/me/requests?top=${top}`),

    adminListCallers: (status?: CallerStatus) =>
      send<Caller[]>('GET', `/admin/callers${status ? `?status=${status}` : ''}`),
    adminApproveCaller: (id: string, monthlyBudgetUsd: number | null) =>
      send<Caller>('POST', `/admin/callers/${id}/approve`, { monthlyBudgetUsd }),
    adminUpdateCaller: (id: string, patch: UpdateCallerRequest) =>
      send<Caller>('PATCH', `/admin/callers/${id}`, patch),
    adminCreateSystemCaller: (req: CreateSystemCallerRequest) =>
      send<Caller>('POST', '/admin/callers/system', req),

    adminListBudgetRequests: (status?: 'Pending' | 'Approved' | 'Rejected') =>
      send<BudgetRequestDetail[]>('GET', `/admin/budget-requests${status ? `?status=${status}` : ''}`),
    adminApproveBudgetRequest: (id: string, body: { approvedBudgetUsd?: number; makeUnlimited?: boolean; notes?: string }) =>
      send<BudgetRequestDetail>('POST', `/admin/budget-requests/${id}/approve`, body),
    adminRejectBudgetRequest: (id: string, notes?: string) =>
      send<BudgetRequestDetail>('POST', `/admin/budget-requests/${id}/reject`, { notes }),

    adminListModels: () => send<AdminModel[]>('GET', '/admin/models'),
    adminUpsertModel: (req: UpsertModelRequest) => send<AdminModel>('PUT', '/admin/models', req),
    adminSetModelEnabled: (id: string, body: SetModelEnabledRequest) =>
      send<AdminModel>('POST', `/admin/models/${id}/enabled`, body),
    adminDeleteModel: (id: string) => send<void>('DELETE', `/admin/models/${id}`),
    // Deactivate catalog models the supplier no longer lists (OpenRouter is exempt).
    adminReconcileModels: (supplier: string) =>
      send<ReconcileResult>('POST', `/admin/models/reconcile/${encodeURIComponent(supplier)}`),

    adminGetGatewayDefault: () => send<GatewayDefault>('GET', '/admin/settings/gateway-default'),
    adminSetGatewayDefault: (req: SetGatewayDefaultRequest) =>
      send<GatewayDefault>('PUT', '/admin/settings/gateway-default', req),

    adminListSuppliers: () => send<SupplierInfo[]>('GET', '/admin/suppliers'),
    adminSetSupplierLicenceKey: (supplier: string, apiKey: string) =>
      send<void>('PUT', `/admin/suppliers/${encodeURIComponent(supplier)}/licence-key`, { apiKey }),
    adminAvailableModels: (supplier: string) =>
      send<AvailableSupplierModel[]>('GET', `/admin/suppliers/${encodeURIComponent(supplier)}/available-models`),
    // OpenRouter isn't bulk-listed (300+ models); resolve one slug at a time for its price.
    adminResolveOpenRouterModel: (slug: string) =>
      send<AvailableSupplierModel>('GET', `/admin/suppliers/openrouter/resolve?slug=${encodeURIComponent(slug)}`),

    adminListMcpServers: () => send<AdminMcpServer[]>('GET', '/admin/mcp-servers'),
    adminUpsertMcpServer: (req: UpsertMcpServerRequest) => send<AdminMcpServer>('PUT', '/admin/mcp-servers', req),
    adminSetMcpServerKey: (name: string, apiKey: string) =>
      send<void>('PUT', `/admin/mcp-servers/${encodeURIComponent(name)}/key`, { apiKey }),
    adminSetMcpServerEnabled: (name: string, enabled: boolean) =>
      send<AdminMcpServer>('POST', `/admin/mcp-servers/${encodeURIComponent(name)}/enabled`, enabled),
    adminMcpServerTools: (name: string) =>
      send<McpServerTool[]>('GET', `/admin/mcp-servers/${encodeURIComponent(name)}/tools`),
    adminToolUsage: (from: string, to: string, callerId?: string) =>
      send<ToolUsageSummaryRow[]>('GET', `/admin/usage/tools?${usageQuery(from, to, callerId)}`),
    adminToolInvocations: (top = 100, callerId?: string) => {
      const qs = new URLSearchParams({ top: String(top) });
      if (callerId) qs.set('callerId', callerId);
      return send<ToolInvocationRow[]>('GET', `/admin/usage/tool-invocations?${qs.toString()}`);
    },

    adminUsageByCaller: (from: string, to: string) =>
      send<UsageGroupRow[]>('GET', `/admin/usage/by-caller?${usageQuery(from, to)}`),
    adminUsageByModel: (from: string, to: string, callerId?: string) =>
      send<UsageGroupRow[]>('GET', `/admin/usage/by-model?${usageQuery(from, to, callerId)}`),
    adminUsageDaily: (from: string, to: string, callerId?: string) =>
      send<DailyUsagePoint[]>('GET', `/admin/usage/daily?${usageQuery(from, to, callerId)}`),
    adminRecentRequests: (top = 100, callerId?: string) => {
      const qs = new URLSearchParams({ top: String(top) });
      if (callerId) qs.set('callerId', callerId);
      return send<RequestLogRow[]>('GET', `/admin/usage/requests?${qs.toString()}`);
    }
  };
}
